import React, { useState, useEffect } from 'react';
import {
  useListJobOpeningsQuery,
  useGetJobOpeningJdUrlMutation,
  useGetJobOpeningVideoUrlMutation,
  useSubmitJobApplicationMutation,
} from '../app/api';
import { VisibilityBadge } from './HRJobOpenings';
import PropTypes from 'prop-types';

const brand = {
  deepBlue:  '#0a2947',
  tealBlue:  '#0069a6',
  goldenrod: '#d09b2c',
  charcoal:  '#58585b',
  softGray:  '#e9e6e1',
  green:     '#1a7f4e',
  red:       '#c0392b',
};

const POSITION_LABELS = { in_office: 'In office', hybrid: 'Hybrid', remote: 'Remote' };
const EMPLOYMENT_LABELS = { full_time: 'Full-time', part_time: 'Part-time' };

// Free-text application questions, in display order. `key` maps to the API body.
const QUESTIONS = [
  { key: 'reason_interested', label: 'Why are you interested in this position?' },
  { key: 'strengths', label: 'What skills, experience and accomplishments make you a strong candidate for this role?' },
  { key: 'growth_alignment', label: 'How does this opportunity align with your professional growth and career goals?' },
];

// Acknowledgements — all three must be checked before the application can be
// submitted (enforced here and re-checked by the API).
const ACKS = [
  { key: 'ack_no_guarantee', label: 'I understand that submitting this application does not guarantee an interview or promotion.' },
  { key: 'ack_transition_coordinated', label: 'I understand that, if selected, my transition date will be coordinated between departments.' },
  { key: 'ack_reviewed_requirements', label: 'I have reviewed the job description and meet the minimum requirements.' },
];

// Intro video shown at the top of a posting tile. Fetches a short-lived
// presigned URL on mount (and whenever the posting id changes) and plays it
// inline. Renders nothing while loading or if the URL can't be fetched.
function JobVideo({ openingId }) {
  const [getVideoUrl] = useGetJobOpeningVideoUrlMutation();
  const [url, setUrl] = useState(null);

  useEffect(() => {
    let active = true;
    setUrl(null);
    getVideoUrl({ id: openingId })
      .unwrap()
      .then((res) => { if (active && res?.url) setUrl(res.url); })
      .catch(() => { /* no-op; tile renders without the video */ });
    return () => { active = false; };
  }, [openingId, getVideoUrl]);

  if (!url) return null;
  return (
    <div style={{ marginBottom: '0.85rem', borderRadius: '10px', overflow: 'hidden', background: '#000' }}>
      <video
        src={url}
        controls
        preload="metadata"
        style={{ width: '100%', display: 'block', maxHeight: '320px', background: '#000' }}
      >
        Your browser does not support embedded video.
      </video>
    </div>
  );
}
JobVideo.propTypes = { openingId: PropTypes.number.isRequired };

function MetaChip({ icon, children }) {
  if (!children) return null;
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: '0.35rem',
      background: '#eef2f6', color: brand.charcoal, fontSize: '0.75rem', fontWeight: 600,
      padding: '0.2rem 0.55rem', borderRadius: '999px',
    }}>
      <i className={`bi bi-${icon}`} /> {children}
    </span>
  );
}
MetaChip.propTypes = {
  icon: PropTypes.string,
  children: PropTypes.string
}

// Modal application form for a single posting. Manages its own answer/ack state;
// calls onSubmitted() once the API accepts the application.
function ApplicationModal({ opening, onClose, onSubmitted }) {
  const [submit, { isLoading }] = useSubmitJobApplicationMutation();
  const [answers, setAnswers] = useState({ reason_interested: '', strengths: '', growth_alignment: '' });
  const [acks, setAcks] = useState({ ack_no_guarantee: false, ack_transition_coordinated: false, ack_reviewed_requirements: false });
  const [error, setError] = useState(null);

  const allAcked = ACKS.every((a) => acks[a.key]);
  const setAnswer = (k, v) => setAnswers((s) => ({ ...s, [k]: v }));
  const toggleAck = (k) => setAcks((s) => ({ ...s, [k]: !s[k] }));

  const onSend = async () => {
    if (!allAcked) return;
    if (QUESTIONS.some((q) => !answers[q.key].trim())) {
      setError('Please answer all three questions before submitting.');
      return;
    }
    setError(null);
    try {
      await submit({ id: opening.id, body: { ...answers, ...acks } }).unwrap();
      onSubmitted();
    } catch (e) {
      setError(e?.data?.detail || 'Could not submit your application. Please try again.');
    }
  };

  const labelStyle = { fontWeight: 700, color: brand.deepBlue, fontSize: '0.85rem', marginBottom: '0.3rem', display: 'block' };
  const inputStyle = { width: '100%', padding: '0.5rem 0.65rem', borderRadius: '8px', border: '1px solid #cdd5dd', fontSize: '0.9rem', minHeight: '5rem', resize: 'vertical' };

  return (
    <div
      onClick={onClose}
      style={{ position: 'fixed', inset: 0, background: 'rgba(10,41,71,0.55)', zIndex: 1200, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', overflowY: 'auto', padding: '2rem 1rem' }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="card"
        style={{ border: 'none', borderRadius: '12px', maxWidth: '720px', width: '100%', boxShadow: '0 8px 40px rgba(10,41,71,0.35)' }}
      >
        <div className="card-body" style={{ padding: '1.5rem' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '1rem' }}>
            <div>
              <p style={{ color: brand.goldenrod, fontWeight: 700, fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '1.5px', margin: 0 }}>Apply</p>
              <h5 style={{ color: brand.deepBlue, fontWeight: 800, margin: '0.1rem 0 0' }}>{opening.job_title}</h5>
            </div>
            <button className="btn btn-sm btn-link p-0" style={{ color: brand.charcoal, fontSize: '1.25rem' }} onClick={onClose} aria-label="Close">
              <i className="bi bi-x-lg" />
            </button>
          </div>

          <hr style={{ margin: '1rem 0' }} />

          {QUESTIONS.map((q) => (
            <div key={q.key} style={{ marginBottom: '1rem' }}>
              <label style={labelStyle}>{q.label}</label>
              <textarea
                style={inputStyle}
                value={answers[q.key]}
                onChange={(e) => setAnswer(q.key, e.target.value)}
                placeholder="Your response…"
              />
            </div>
          ))}

          {/* ── Acknowledgements ─────────────────────────────────────────────── */}
          <div style={{ marginTop: '1.25rem', padding: '1rem', borderRadius: '10px', background: '#f4f1ec', border: '1px solid #e2ddd4' }}>
            <label style={{ ...labelStyle, marginBottom: '0.6rem' }}>
              <i className="bi bi-check2-square me-1" /> Acknowledgements
            </label>
            {ACKS.map((a) => (
              <div key={a.key} className="form-check" style={{ marginBottom: '0.5rem' }}>
                <input
                  className="form-check-input"
                  type="checkbox"
                  id={`ack-${a.key}`}
                  checked={acks[a.key]}
                  onChange={() => toggleAck(a.key)}
                />
                <label className="form-check-label" htmlFor={`ack-${a.key}`} style={{ color: brand.charcoal, fontSize: '0.85rem' }}>
                  {a.label}
                </label>
              </div>
            ))}
          </div>

          {error && (
            <div style={{ marginTop: '1rem', color: brand.red, fontSize: '0.85rem', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
              <i className="bi bi-exclamation-circle" /> {error}
            </div>
          )}

          <div style={{ marginTop: '1.25rem', display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
            <button
              className="btn btn-sm"
              style={{ background: allAcked ? brand.deepBlue : '#9aa6b1', color: 'white', fontWeight: 700, cursor: allAcked ? 'pointer' : 'not-allowed' }}
              onClick={onSend}
              disabled={!allAcked || isLoading}
              title={allAcked ? undefined : 'Check all acknowledgements to submit'}
            >
              {isLoading ? 'Submitting…' : 'Submit application'}
            </button>
            <button className="btn btn-sm btn-light" onClick={onClose} disabled={isLoading}>Cancel</button>
            {!allAcked && (
              <span style={{ color: brand.charcoal, fontSize: '0.78rem' }}>
                All acknowledgements are required to submit.
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
ApplicationModal.propTypes = {
  opening: PropTypes.object.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmitted: PropTypes.func.isRequired,
};

export default function JobOpenings() {
  const { data, isLoading } = useListJobOpeningsQuery();
  const openings = data?.openings || [];
  /*const isManager = Boolean(data?.is_manager);*/
  const [getJdUrl] = useGetJobOpeningJdUrlMutation();

  // The posting currently being applied to (drives the modal), and the set of
  // postings applied to in this session (for the "Applied" confirmation).
  const [applyOpening, setApplyOpening] = useState(null);
  const [appliedIds, setAppliedIds] = useState(() => new Set());

  const onDownload = async (id) => {
    try {
      const { url } = await getJdUrl({ id }).unwrap();
      if (url) window.open(url, '_blank', 'noopener');
    } catch {
      /* no-op; the file may have been removed */
    }
  };

  const onSubmitted = () => {
    if (applyOpening) {
      setAppliedIds((prev) => new Set(prev).add(applyOpening.id));
    }
    setApplyOpening(null);
  };

  return (
    <div style={{ background: brand.softGray, minHeight: '100vh', padding: '1.5rem' }}>
      <div style={{ marginBottom: '1.25rem' }}>
        <h4 style={{ color: brand.deepBlue, fontWeight: 800, margin: 0 }}>Career Opportunities</h4>
        <p style={{ color: brand.charcoal, margin: 0, fontSize: '0.875rem' }}>
        </p>
      </div>

      {isLoading ? (
        <div className="text-center py-5"><div className="spinner-border text-primary" role="status" /></div>
      ) : openings.length === 0 ? (
        <div className="card" style={{ border: 'none', boxShadow: '0 2px 12px rgba(10,41,71,0.1)', borderRadius: '12px' }}>
          <div className="card-body" style={{ color: brand.charcoal, fontSize: '0.9rem' }}>
            There are no open positions to show right now.
          </div>
        </div>
      ) : (
        <div className="row g-3">
          {openings.map((o) => {
            const applied = o.has_applied || appliedIds.has(o.id);
            return (
            <div className="col-md-6" key={o.id}>
              <div className="card h-100" style={{ border: 'none', boxShadow: '0 2px 12px rgba(10,41,71,0.1)', borderRadius: '12px' }}>
                <div className="card-body">
                  {o.video_filename && <JobVideo openingId={o.id} />}
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '0.75rem' }}>
                    <h5 style={{ color: brand.deepBlue, fontWeight: 800, margin: 0, fontSize: '1.1rem' }}>{o.job_title}</h5>
                    <VisibilityBadge visibility={o.visibility} />
                  </div>

                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem', margin: '0.75rem 0' }}>
                    <MetaChip icon="geo-alt">{o.location}</MetaChip>
                    <MetaChip icon="building">{POSITION_LABELS[o.position_type]}</MetaChip>
                    <MetaChip icon="clock">{EMPLOYMENT_LABELS[o.employment_type]}</MetaChip>
                    {o.is_intern && <MetaChip icon="mortarboard">Internship</MetaChip>}
                    <MetaChip icon="person-badge">{o.manager_name ? `Manager: ${o.manager_name}` : null}</MetaChip>
                    <MetaChip icon="calendar-event">{o.potential_start_date ? `Starts ${o.potential_start_date}` : null}</MetaChip>
                  </div>

                  {o.description && (
                    <p style={{ color: brand.charcoal, fontSize: '0.875rem', whiteSpace: 'pre-wrap', margin: '0 0 0.75rem' }}>
                      {o.description}
                    </p>
                  )}

                  {applied && (
                    <div style={{
                      display: 'flex', alignItems: 'center', gap: '0.5rem',
                      background: '#eaf5ef', border: `1px solid ${brand.green}`,
                      color: brand.green, fontSize: '0.85rem', fontWeight: 600,
                      padding: '0.6rem 0.75rem', borderRadius: '10px', margin: '0 0 0.75rem',
                    }}>
                      <i className="bi bi-check-circle-fill" />
                      Thank you for your application, HR will be in contact with you soon.
                    </div>
                  )}

                  <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'center' }}>
                    {o.jd_filename && (
                      <button
                        className="btn btn-sm"
                        style={{ background: brand.tealBlue, color: 'white', fontWeight: 700 }}
                        onClick={() => onDownload(o.id)}
                      >
                        <i className="bi bi-file-earmark-text me-1" /> Full job description
                      </button>
                    )}
                    {applied ? (
                      <button
                        className="btn btn-sm"
                        style={{ background: 'white', color: brand.green, fontWeight: 700, border: `1px solid ${brand.green}` }}
                        onClick={() => setApplyOpening(o)}
                        title="You've applied — submit again to update your answers"
                      >
                        <i className="bi bi-check-circle-fill me-1" /> Applied — update
                      </button>
                    ) : (
                      <button
                        className="btn btn-sm"
                        style={{ background: brand.goldenrod, color: brand.deepBlue, fontWeight: 700 }}
                        onClick={() => setApplyOpening(o)}
                      >
                        <i className="bi bi-pencil-square me-1" /> Apply
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
            );
          })}
        </div>
      )}

      {applyOpening && (
        <ApplicationModal
          opening={applyOpening}
          onClose={() => setApplyOpening(null)}
          onSubmitted={onSubmitted}
        />
      )}
    </div>
  );
}
